import axios from "axios";
import { config } from "../config/Config";

const BASE = `${config.serverBaseUrl}/v1/notifications`;

const JSON_HEADERS = { "Content-Type": "application/json" };

/**
 * Cross-component signal that the unread count may have changed. Fired by
 * every mutation helper below on success. Subscribers (e.g. the header
 * bell badge via {@code useUnreadNotificationCount}) should re-fetch
 * {@link getUnreadCount}.
 * <p>
 * Why an event rather than shared state: the Notifications page and the
 * header bell live in completely different React trees, and the bell hook
 * was previously decoupled from the page's local feed state — so when the
 * page auto-marked things read, the bell badge stayed stuck. Routing all
 * mutations through the service layer means future callers (deep-link
 * handlers, the bell drawer, etc.) don't need to remember to signal.
 */
const UNREAD_CHANGED_EVENT = "nc:unread-changed";

const dispatchUnreadChanged = () => {
    if (typeof window === "undefined") return;
    try {
        window.dispatchEvent(new Event(UNREAD_CHANGED_EVENT));
    } catch {
        // Some very old environments (or SSR shims) may not have Event;
        // silently no-op rather than crash the mutation that just succeeded.
    }
};

export const subscribeUnreadChanged = (callback) => {
    if (typeof window === "undefined" || typeof callback !== "function") {
        return () => {};
    }
    window.addEventListener(UNREAD_CHANGED_EVENT, callback);
    return () => window.removeEventListener(UNREAD_CHANGED_EVENT, callback);
};

/**
 * Signal that specific notifications were actioned (read / dismissed), so any
 * surface showing a persistent toast for one of them can clear it. `ids` lists
 * the affected notification ids; `all: true` means a bulk/mark-all operation
 * actioned everything matching the current filter. Consumed by
 * <AccessGrantedToast/> so its "Refresh now" toast disappears once the user
 * handles the underlying notification from the Notifications surface.
 */
const NOTIFICATION_ACTIONED_EVENT = "gp:notification-actioned";

const dispatchActioned = (detail) => {
    if (typeof window === "undefined") return;
    try {
        window.dispatchEvent(new CustomEvent(NOTIFICATION_ACTIONED_EVENT, { detail }));
    } catch {
        // older environments without CustomEvent — non-fatal
    }
};

/**
 * Build a query string from a filter object, skipping empties. Modules and
 * severities repeat (`module=STORE&module=CALENDAR`) because Spring binds
 * `List<Module>` from duplicated params.
 */
function buildFeedParams({ modules, severities, statuses, cursor, limit, includeDismissed, from, to, q } = {}) {
    const params = new URLSearchParams();
    (modules || []).forEach((m) => params.append("module", m));
    (severities || []).forEach((s) => params.append("severity", s));
    // Read-state filter — UNREAD / REVIEWED (Action-required + read). Backend binds List<Status>.
    (statuses || []).forEach((s) => params.append("status", s));
    if (cursor) params.set("cursor", cursor);
    if (limit) params.set("limit", String(limit));
    if (includeDismissed) params.set("includeDismissed", "true");
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (q && q.trim()) params.set("q", q.trim());
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

export const getNotificationFeed = async (filter) => {
    try {
        const response = await axios.get(`${BASE}${buildFeedParams(filter)}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching notification feed:", error.response?.data || error.message);
        throw error;
    }
};

export const getUnreadCount = async () => {
    try {
        const response = await axios.get(`${BASE}/unread-count`);
        return response.data?.unread ?? 0;
    } catch (error) {
        console.error("Error fetching unread count:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Per-game unread breakdown. Returns a `{ gameId: count }` map; workspace-wide
 * notifications collapse under the `_workspace` key. Aggregated server-side from
 * Mongo â€” slower than {@link getUnreadCount}, call on demand (e.g. when the
 * bell drawer opens) rather than on every render.
 */
export const getUnreadCountByGame = async () => {
    try {
        const response = await axios.get(`${BASE}/unread-count-by-game`);
        return response.data?.counts ?? {};
    } catch (error) {
        console.error("Error fetching per-game unread counts:", error.response?.data || error.message);
        throw error;
    }
};

export const getNotificationById = async (id) => {
    try {
        const response = await axios.get(`${BASE}/${encodeURIComponent(id)}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching notification:", error.response?.data || error.message);
        throw error;
    }
};

export const markNotificationRead = async (id) => {
    try {
        const response = await axios.post(`${BASE}/${encodeURIComponent(id)}/read`, null, {
            headers: JSON_HEADERS,
        });
        dispatchUnreadChanged();
        dispatchActioned({ ids: [id] });
        return response.data;
    } catch (error) {
        console.error("Error marking notification read:", error.response?.data || error.message);
        throw error;
    }
};

export const dismissNotification = async (id) => {
    try {
        const response = await axios.post(`${BASE}/${encodeURIComponent(id)}/dismiss`, null, {
            headers: JSON_HEADERS,
        });
        dispatchUnreadChanged();
        dispatchActioned({ ids: [id] });
        return response.data;
    } catch (error) {
        console.error("Error dismissing notification:", error.response?.data || error.message);
        throw error;
    }
};

export const autoReadVisible = async () => {
    try {
        const response = await axios.post(`${BASE}/auto-read`, null, { headers: JSON_HEADERS });
        dispatchUnreadChanged();
        dispatchActioned({ all: true });
        return response.data;
    } catch (error) {
        console.error("Error auto-reading notifications:", error.response?.data || error.message);
        throw error;
    }
};

export const markAllRead = async ({ modules, severities, includeDismissed } = {}) => {
    try {
        const response = await axios.post(
            `${BASE}/mark-all-read`,
            { modules: modules || null, severities: severities || null, includeDismissed: !!includeDismissed },
            { headers: JSON_HEADERS }
        );
        dispatchUnreadChanged();
        dispatchActioned({ all: true });
        return response.data;
    } catch (error) {
        console.error("Error marking all read:", error.response?.data || error.message);
        throw error;
    }
};

export const bulkMarkRead = async (ids) => {
    try {
        const response = await axios.post(
            `${BASE}/bulk-read`,
            { ids },
            { headers: JSON_HEADERS }
        );
        dispatchUnreadChanged();
        dispatchActioned({ ids });
        return response.data;
    } catch (error) {
        console.error("Error bulk-marking read:", error.response?.data || error.message);
        throw error;
    }
};

export const bulkDismiss = async (ids) => {
    try {
        const response = await axios.post(
            `${BASE}/bulk-dismiss`,
            { ids },
            { headers: JSON_HEADERS }
        );
        dispatchUnreadChanged();
        dispatchActioned({ ids });
        return response.data;
    } catch (error) {
        console.error("Error bulk-dismissing:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Returns the CSV export URL for a given filter. Server enforces admin access.
 * Callers trigger a download by setting `window.location.href = url` (cookies
 * are not attached by browsers for cross-origin GET, so for auth'd downloads
 * use {@link exportCsvBlob} instead which goes through axios).
 */
export const buildExportCsvUrl = (filter = {}) => {
    const params = new URLSearchParams();
    (filter.modules || []).forEach((m) => params.append("module", m));
    (filter.severities || []).forEach((s) => params.append("severity", s));
    if (filter.from) params.set("from", filter.from);
    if (filter.to) params.set("to", filter.to);
    if (filter.maxRows) params.set("maxRows", String(filter.maxRows));
    const qs = params.toString();
    return `${BASE}/export.csv${qs ? `?${qs}` : ""}`;
};

/**
 * Downloads the CSV through axios so the auth interceptor attaches the
 * Firebase ID token and X-Web-Org-Id headers. Returns a Blob for the caller
 * to save with an <a download> trick.
 */
export const exportCsvBlob = async (filter) => {
    try {
        const response = await axios.get(buildExportCsvUrl(filter), { responseType: "blob" });
        return response.data;
    } catch (error) {
        console.error("Error exporting CSV:", error.response?.data || error.message);
        throw error;
    }
};
